import { useMemo } from 'react';

import { Button } from '~components/generic/editor/Button/Button';
import { ButtonGroup } from '~components/generic/editor/ButtonGroup/ButtonGroup';
import { ArrowDownIcon, ArrowUpIcon, CheckIcon, CloseIcon, PenIcon } from '~components/icons';
import { DeleteModulePopover } from '~components/page/EditmodeHeader/DeleteModulePopover';
import useLabels from '~hooks/useLabels';
import { ModuleEditMenu } from '../ModuleEditMenu/ModuleEditMenu';
import { getOverlayTitle } from '../ModuleOverrides';
import { BorderBottom, BorderLeft, BorderRight, BorderTop } from '../OverlayBorder/OverlayBorder';

export type ModuleEditOverlayProps = {
  isEditing: boolean;
  moduleTitle: string;
  moduleIcon?: string;
  paddingTopType: '' | 'big' | 'small' | 'none';
  paddingBottomType: '' | 'big' | 'small' | 'none';
  isProcessing: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onStopEdit: () => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  showSettings?: boolean;
};

export const ModuleEditOverlay = ({
  isEditing,
  moduleIcon,
  moduleTitle,
  paddingTopType,
  paddingBottomType,
  isProcessing,
  canEdit,
  canDelete = true,
  canMoveUp,
  canMoveDown,
  onStopEdit,
  onStartEdit,
  onSaveEdit,
  onMoveUp,
  onMoveDown,
  onDelete,
  showSettings,
}: ModuleEditOverlayProps) => {
  const { msgSaveModuleHint, msgCancelModuleHint, msgMoveDownModuleHint, msgMoveUpModuleHint, msgEditModuleHint } =
    useLabels([
      'msgSaveModuleHint',
      'msgDeleteModuleHint',
      'msgCancelModuleHint',
      'msgMoveDownModuleHint',
      'msgMoveUpModuleHint',
      'msgEditModuleHint',
    ]);

  const showLoading = isProcessing;
  const showEdit = !showLoading && !isEditing && canEdit;

  const showMoveDown = !showLoading && !isEditing && canMoveDown;
  const showMoveUp = !showLoading && !isEditing && canMoveUp;
  const showCancelDeleteSave = !showLoading && isEditing;
  const showDelete = canDelete;
  const showBorders = isEditing || showSettings;

  const txtTitle = useMemo(() => getOverlayTitle(moduleTitle), [moduleTitle]);

  return (
    <div data-testid="module-edit-overlay">
      <ModuleEditMenu title={moduleTitle} show={isEditing}>
        <ButtonGroup label={txtTitle} preIcon={moduleIcon}>
          {showEdit && <Button title={msgEditModuleHint} icon={<PenIcon />} onClick={onStartEdit} />}

          {showMoveUp && <Button title={msgMoveUpModuleHint} onClick={onMoveUp} icon={<ArrowUpIcon />} />}
          {showMoveDown && <Button title={msgMoveDownModuleHint} onClick={onMoveDown} icon={<ArrowDownIcon />} />}
          {showCancelDeleteSave && showDelete && <DeleteModulePopover onDelete={onDelete} />}
          {showCancelDeleteSave && <Button title={msgCancelModuleHint} onClick={onStopEdit} icon={<CloseIcon />} />}
          {showCancelDeleteSave && <Button title={msgSaveModuleHint} onClick={onSaveEdit} icon={<CheckIcon />} />}
        </ButtonGroup>
      </ModuleEditMenu>
      <BorderTop show={showBorders} size={paddingTopType} />
      <BorderBottom show={showBorders} size={paddingBottomType} />
      <BorderLeft show={showBorders} />
      <BorderRight show={showBorders} />
    </div>
  );
};
